/**
 * SPOResultPage - SPO结果管理页面
 *
 * 功能：
 * 1. SPO草稿管理（S&P上传草稿）
 * 2. 草稿预览与下载
 * 3. 发行人反馈表单
 * 4. 发布日期协商
 * 5. 最终报告上传与管理
 * 6. 报告归档与通知
 *
 * 权限控制：
 * - 运营商：上传草稿、上传最终报告、管理全流程
 * - 发行人：查看草稿、提交反馈
 * - 管理员：完全权限
 * - 其他角色：仅查看最终报告
 */

// @ts-nocheck - TODO: Fix type definitions
import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Alert,
  Modal,
  message,
  Spin,
  Divider,
  Row,
  Col,
  Typography,
  Empty,
  Upload,
  DatePicker,
  Steps,
  Descriptions,
  Tag,
  Rate,
  Tabs,
} from 'antd';
import {
  FileTextOutlined,
  UploadOutlined,
  EyeOutlined,
  DownloadOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  FileDoneOutlined,
  EditOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';
import {
  useGetSPOQuery,
  useUploadDraftMutation,
  useSubmitFeedbackMutation,
  useUploadReportMutation,
} from '../../store/api/spoApi';
import { UnifiedStatusTag } from '../../components/unified';
import StandardPageLayout from '../../components/layout/StandardPageLayout';
import { useResolvedSpoId } from './hooks/useResolvedSpoId';

const { Step } = Steps;
const { TextArea } = Input;
const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface SPOResultPageProps {
  projectId?: string;
}

const SPOResultPage: React.FC<SPOResultPageProps> = ({
  projectId: propProjectId,
}) => {
  const { resolvedSpoId, isResolving, isResolveError } = useResolvedSpoId({
    projectId: propProjectId,
  });

  // ==================== State管理 ====================
  const [draftForm] = Form.useForm();
  const [feedbackForm] = Form.useForm();
  const [reportForm] = Form.useForm();
  const [draftModalVisible, setDraftModalVisible] = useState(false);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [draftFiles, setDraftFiles] = useState<UploadFile[]>([]);
  const [reportFiles, setReportFiles] = useState<UploadFile[]>([]);

  // ==================== API Hooks ====================
  const {
    data: spoData,
    isLoading: spoLoading,
    error: spoError,
    refetch: refetchSPO,
  } = useGetSPOQuery(resolvedSpoId!, {
    skip: !resolvedSpoId,
  });

  const [uploadDraft, { isLoading: uploadingDraft }] = useUploadDraftMutation();
  const [submitFeedback, { isLoading: submittingFeedback }] =
    useSubmitFeedbackMutation();
  const [uploadReport, { isLoading: uploadingReport }] =
    useUploadReportMutation();

  // ==================== 权限控制 ====================
  const userRole = 'operator'; // TODO: 从Redux Store获取实际用户角色
  const canUploadDraft = userRole === 'operator' || userRole === 'admin';
  const canSubmitFeedback = userRole === 'issuer' || userRole === 'admin';
  const canUploadReport = userRole === 'operator' || userRole === 'admin';

  // ==================== 文件上传配置 ====================
  const draftUploadProps = {
    fileList: draftFiles,
    beforeUpload: (file: any) => {
      const isPdf = file.type === 'application/pdf';
      if (!isPdf) {
        message.error('只能上传PDF文件！');
        return Upload.LIST_IGNORE;
      }
      const isLt20M = file.size / 1024 / 1024 < 20;
      if (!isLt20M) {
        message.error('文件大小不能超过20MB！');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: (info: any) => {
      setDraftFiles(info.fileList);
    },
    onRemove: (file: any) => {
      setDraftFiles(prev => prev.filter(item => item.uid !== file.uid));
    },
  };

  const reportUploadProps = {
    fileList: reportFiles,
    beforeUpload: (file: any) => {
      const isPdf = file.type === 'application/pdf';
      if (!isPdf) {
        message.error('只能上传PDF文件！');
        return Upload.LIST_IGNORE;
      }
      const isLt20M = file.size / 1024 / 1024 < 20;
      if (!isLt20M) {
        message.error('文件大小不能超过20MB！');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: (info: any) => {
      setReportFiles(info.fileList);
    },
    onRemove: (file: any) => {
      setReportFiles(prev => prev.filter(item => item.uid !== file.uid));
    },
  };

  // ==================== 上传草稿 ====================
  const handleUploadDraft = async () => {
    try {
      const values = await draftForm.validateFields();

      if (draftFiles.length === 0) {
        message.error('请上传SPO草稿文件！');
        return;
      }

      const draftData = {
        draftVersion: values.draftVersion,
        draftDocumentUrl: 'https://example.com/drafts/' + draftFiles[0].uid, // TODO: 实际文件URL
        notes: values.notes,
      };

      const result = await uploadDraft({
        spoId: resolvedSpoId!,
        data: draftData,
      }).unwrap();

      if (result.success) {
        message.success('SPO草稿上传成功！');
        setDraftModalVisible(false);
        draftForm.resetFields();
        setDraftFiles([]);
        refetchSPO();
      } else {
        message.error(result.error?.message || '上传失败');
      }
    } catch (error) {
      console.error('Upload draft error:', error);
      message.error('上传失败，请检查表单');
    }
  };

  // ==================== 提交反馈 ====================
  const handleSubmitFeedback = async () => {
    try {
      const values = await feedbackForm.validateFields();

      const feedbackData = {
        overallRating: values.overallRating,
        accuracyRating: values.accuracyRating,
        comments: values.comments,
        revisionRequests: values.revisionRequests,
      };

      const result = await submitFeedback({
        spoId: resolvedSpoId!,
        data: feedbackData,
      }).unwrap();

      if (result.success) {
        message.success('反馈提交成功！');
        setFeedbackModalVisible(false);
        feedbackForm.resetFields();
        refetchSPO();
      } else {
        message.error(result.error?.message || '提交失败');
      }
    } catch (error) {
      console.error('Submit feedback error:', error);
      message.error('提交失败，请检查表单');
    }
  };

  // ==================== 上传最终报告 ====================
  const handleUploadReport = async () => {
    try {
      const values = await reportForm.validateFields();

      if (reportFiles.length === 0) {
        message.error('请上传最终SPO报告文件！');
        return;
      }

      const reportData = {
        reportVersion: values.reportVersion,
        reportDocumentUrl: 'https://example.com/reports/' + reportFiles[0].uid, // TODO: 实际文件URL
        publishDate: values.publishDate.toISOString(),
        publicationNotes: values.publicationNotes,
      };

      const result = await uploadReport({
        spoId: resolvedSpoId!,
        data: reportData,
      }).unwrap();

      if (result.success) {
        message.success('最终SPO报告上传成功！');
        setReportModalVisible(false);
        reportForm.resetFields();
        setReportFiles([]);
        refetchSPO();
      } else {
        message.error(result.error?.message || '上传失败');
      }
    } catch (error) {
      console.error('Upload report error:', error);
      message.error('上传失败，请检查表单');
    }
  };

  // ==================== 预览文档 ====================
  const handlePreview = (url: string) => {
    setPreviewUrl(url);
    setPreviewModalVisible(true);
  };

  // ==================== 渲染流程步骤 ====================
  const renderSteps = () => {
    const resultData = spoData?.data?.result || {};
    const currentStep = resultData.draftIssuedAt
      ? resultData.feedbackReceivedAt
        ? resultData.finalIssuedAt
          ? 3
          : 2
        : 1
      : 0;

    return (
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step
          title="草稿发布"
          description={
            resultData.draftIssuedAt
              ? dayjs(resultData.draftIssuedAt).format('YYYY-MM-DD')
              : '等待上传'
          }
          icon={<FileTextOutlined />}
        />
        <Step
          title="发行人反馈"
          description={
            resultData.feedbackReceivedAt
              ? dayjs(resultData.feedbackReceivedAt).format('YYYY-MM-DD')
              : '等待反馈'
          }
          icon={<EditOutlined />}
        />
        <Step
          title="最终报告"
          description={
            resultData.finalIssuedAt
              ? dayjs(resultData.finalIssuedAt).format('YYYY-MM-DD')
              : '等待发布'
          }
          icon={<FileDoneOutlined />}
        />
      </Steps>
    );
  };

  // ==================== 渲染草稿管理 ====================
  const renderDraftManagement = () => {
    const resultData = spoData?.data?.result || {};

    return (
      <Card
        title="SPO草稿管理"
        extra={
          canUploadDraft && (
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setDraftModalVisible(true)}
            >
              上传草稿
            </Button>
          )
        }
      >
        {resultData.draftDocumentUrl ? (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="草稿版本" span={1}>
                <Tag color="blue">v{resultData.draftVersion || '1.0'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="发布时间" span={1}>
                {dayjs(resultData.draftIssuedAt).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="文档状态" span={2}>
                <UnifiedStatusTag
                  type="resultStatus"
                  status={
                    resultData.feedbackReceivedAt
                      ? 'FEEDBACK_RECEIVED'
                      : 'DRAFT_ISSUED'
                  }
                />
              </Descriptions.Item>
            </Descriptions>

            <Space>
              <Button
                icon={<EyeOutlined />}
                onClick={() => handlePreview(resultData.draftDocumentUrl)}
              >
                预览草稿
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() =>
                  window.open(resultData.draftDocumentUrl, '_blank')
                }
              >
                下载草稿
              </Button>
            </Space>
          </Space>
        ) : (
          <Empty description="尚未上传SPO草稿" />
        )}
      </Card>
    );
  };

  // ==================== 渲染发行人反馈 ====================
  const renderIssuerFeedback = () => {
    const resultData = spoData?.data?.result || {};

    if (!resultData.draftDocumentUrl) {
      return (
        <Card title="发行人反馈">
          <Alert
            message="等待草稿"
            description="请等待S&P上传SPO草稿后再提交反馈"
            type="info"
            showIcon
          />
        </Card>
      );
    }

    return (
      <Card
        title="发行人反馈"
        extra={
          canSubmitFeedback &&
          !resultData.feedbackReceivedAt && (
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => setFeedbackModalVisible(true)}
            >
              提交反馈
            </Button>
          )
        }
      >
        {resultData.feedbackReceivedAt ? (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="整体评分">
                <Rate disabled value={resultData.overallRating || 0} />
              </Descriptions.Item>
              <Descriptions.Item label="准确性评分">
                <Rate disabled value={resultData.accuracyRating || 0} />
              </Descriptions.Item>
              <Descriptions.Item label="反馈时间" span={2}>
                {dayjs(resultData.feedbackReceivedAt).format(
                  'YYYY-MM-DD HH:mm'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="意见与建议" span={2}>
                <Paragraph>{resultData.issuerComments || '无'}</Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="修改请求" span={2}>
                <Paragraph>{resultData.revisionRequests || '无'}</Paragraph>
              </Descriptions.Item>
            </Descriptions>
          </Space>
        ) : (
          <Empty description="尚未提交反馈" />
        )}
      </Card>
    );
  };

  // ==================== 渲染最终报告 ====================
  const renderFinalReport = () => {
    const resultData = spoData?.data?.result || {};

    return (
      <Card
        title="最终SPO报告"
        extra={
          canUploadReport && (
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setReportModalVisible(true)}
              disabled={!resultData.feedbackReceivedAt}
            >
              上传最终报告
            </Button>
          )
        }
      >
        {resultData.finalReportDocumentUrl ? (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
              message="SPO报告已发布"
              description="最终SPO报告已成功发布，所有相关方已收到通知"
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
            />

            <Descriptions bordered column={2}>
              <Descriptions.Item label="报告版本" span={1}>
                <Tag color="green">
                  v{resultData.finalReportVersion || '1.0'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="发布时间" span={1}>
                {dayjs(resultData.finalIssuedAt).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="计划发布日期" span={1}>
                {resultData.plannedPublicationDate
                  ? dayjs(resultData.plannedPublicationDate).format(
                      'YYYY-MM-DD'
                    )
                  : '未设定'}
              </Descriptions.Item>
              <Descriptions.Item label="报告状态" span={1}>
                <UnifiedStatusTag type="resultStatus" status="FINAL_ISSUED" />
              </Descriptions.Item>
              <Descriptions.Item label="发布说明" span={2}>
                <Paragraph>
                  {resultData.publicationNotes || '无特别说明'}
                </Paragraph>
              </Descriptions.Item>
            </Descriptions>

            <Space>
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={() => handlePreview(resultData.finalReportDocumentUrl)}
              >
                预览报告
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() =>
                  window.open(resultData.finalReportDocumentUrl, '_blank')
                }
              >
                下载报告
              </Button>
            </Space>
          </Space>
        ) : (
          <Empty
            description={
              resultData.feedbackReceivedAt
                ? '等待上传最终报告'
                : '请先完成发行人反馈'
            }
          />
        )}
      </Card>
    );
  };

  // ==================== Loading/Error处理 ====================
  if (isResolving || spoLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>加载SPO结果信息中...</div>
      </div>
    );
  }

  if (spoError || isResolveError || !resolvedSpoId) {
    return (
      <Alert
        message="加载失败"
        description="无法加载SPO流程信息，请刷新页面重试"
        type="error"
        showIcon
      />
    );
  }

  // ==================== 主渲染 ====================
  return (
    <StandardPageLayout
      config={{
        title: 'SPO结果管理',
        description: 'SPO草稿、发行人反馈与最终报告管理',
      }}
    >
      {/* 流程步骤 */}
      {renderSteps()}

      {/* 标签页 */}
      <Tabs defaultActiveKey="draft">
        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              草稿管理
            </span>
          }
          key="draft"
        >
          {renderDraftManagement()}
        </TabPane>

        <TabPane
          tab={
            <span>
              <EditOutlined />
              发行人反馈
            </span>
          }
          key="feedback"
        >
          {renderIssuerFeedback()}
        </TabPane>

        <TabPane
          tab={
            <span>
              <FileDoneOutlined />
              最终报告
            </span>
          }
          key="report"
        >
          {renderFinalReport()}
        </TabPane>
      </Tabs>

      {/* 上传草稿Modal */}
      <Modal
        open={draftModalVisible}
        title="上传SPO草稿"
        onOk={handleUploadDraft}
        onCancel={() => {
          setDraftModalVisible(false);
          draftForm.resetFields();
          setDraftFiles([]);
        }}
        okText="确认上传"
        cancelText="取消"
        width={700}
        confirmLoading={uploadingDraft}
      >
        <Alert
          message="草稿上传说明"
          description="请上传S&P分析师提供的SPO草稿文档（PDF格式）"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={draftForm} layout="vertical">
          <Form.Item
            label="草稿版本"
            name="draftVersion"
            rules={[{ required: true, message: '请输入草稿版本号' }]}
            initialValue="1.0"
          >
            <Input placeholder="如：1.0, 1.1" />
          </Form.Item>

          <Form.Item label="草稿文档" required>
            <Dragger {...draftUploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽PDF文件到此区域上传</p>
              <p className="ant-upload-hint">
                仅支持PDF格式，单个文件不超过20MB
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item label="备注说明" name="notes">
            <TextArea
              rows={3}
              placeholder="可选填写关于此草稿的说明"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 提交反馈Modal */}
      <Modal
        open={feedbackModalVisible}
        title="提交SPO草稿反馈"
        onOk={handleSubmitFeedback}
        onCancel={() => {
          setFeedbackModalVisible(false);
          feedbackForm.resetFields();
        }}
        okText="提交反馈"
        cancelText="取消"
        width={700}
        confirmLoading={submittingFeedback}
      >
        <Alert
          message="反馈提交说明"
          description="请仔细审阅SPO草稿，并提供您的意见与建议"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={feedbackForm} layout="vertical">
          <Form.Item
            label="整体评分"
            name="overallRating"
            rules={[{ required: true, message: '请对草稿进行整体评分' }]}
          >
            <Rate />
          </Form.Item>

          <Form.Item
            label="准确性评分"
            name="accuracyRating"
            rules={[{ required: true, message: '请对内容准确性进行评分' }]}
          >
            <Rate />
          </Form.Item>

          <Form.Item
            label="意见与建议"
            name="comments"
            rules={[
              { required: true, message: '请填写您的意见与建议' },
              { min: 20, message: '意见至少20个字符' },
            ]}
          >
            <TextArea
              rows={5}
              placeholder="请详细描述您对SPO草稿的意见与建议"
              maxLength={2000}
              showCount
            />
          </Form.Item>

          <Form.Item label="修改请求" name="revisionRequests">
            <TextArea
              rows={4}
              placeholder="如有需要修改的内容，请在此详细说明"
              maxLength={1000}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 上传最终报告Modal */}
      <Modal
        open={reportModalVisible}
        title="上传最终SPO报告"
        onOk={handleUploadReport}
        onCancel={() => {
          setReportModalVisible(false);
          reportForm.resetFields();
          setReportFiles([]);
        }}
        okText="确认上传"
        cancelText="取消"
        width={700}
        confirmLoading={uploadingReport}
      >
        <Alert
          message="最终报告上传说明"
          description="请上传S&P提供的最终SPO报告文档（PDF格式）"
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={reportForm} layout="vertical">
          <Form.Item
            label="报告版本"
            name="reportVersion"
            rules={[{ required: true, message: '请输入报告版本号' }]}
            initialValue="1.0"
          >
            <Input placeholder="如：1.0" />
          </Form.Item>

          <Form.Item label="报告文档" required>
            <Dragger {...reportUploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽PDF文件到此区域上传</p>
              <p className="ant-upload-hint">
                仅支持PDF格式，单个文件不超过20MB
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item
            label="计划发布日期"
            name="publishDate"
            rules={[{ required: true, message: '请选择发布日期' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="发布说明" name="publicationNotes">
            <TextArea
              rows={3}
              placeholder="可选填写关于报告发布的说明"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 文档预览Modal */}
      <Modal
        open={previewModalVisible}
        title="文档预览"
        footer={null}
        onCancel={() => setPreviewModalVisible(false)}
        width="90%"
        style={{ top: 20 }}
      >
        <div style={{ height: '70vh', overflow: 'auto' }}>
          {previewUrl ? (
            <iframe
              src={previewUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Document Preview"
            />
          ) : (
            <Empty description="无法加载预览" />
          )}
        </div>
      </Modal>
    </StandardPageLayout>
  );
};

export default SPOResultPage;
